// @ts-nocheck
import React, { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Box, Typography, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, IconButton, Tooltip, Snackbar, Alert, Chip, Checkbox, TextField, Button
} from '@mui/material'
import { PictureAsPdf as PictureAsPdfIcon, TableView as TableViewIcon } from '@mui/icons-material'
import {
  loadFkspRequests,
  downloadFkspPdf,
  markFkspRequestProcessed,
  unmarkFkspRequestProcessed,
  updateFkspFinancistFields,
} from '../lib/apiPhpAdapter'
import { FkspRequest } from '../types'
import { format } from 'date-fns'
import { FkspRequestModal } from '../components/FkspRequestModal'
import { useAuth } from '../hooks/useAuth'
import { useDeepLinkById } from '../hooks/useDeepLinkById'
import { hasFinancistAccess } from '../lib/roleFinancialAccess'
import { FkspRequestsToolbar } from '../components/FkspRequestsToolbar'
import { FkspTablePagination, fkspPageSlice } from '../components/FkspTablePagination'
import { filterAndSortFkspRequests } from '../lib/fkspListFilters'
import { fkspStatusChipColor, fkspStatusLabelCs } from '../lib/fkspStatusLabels'
import { buildFkspEkonomExportRows, downloadFkspEkonomListExport } from '../lib/fkspEkonomExport'
import type {
  FkspProcessedFilter,
  FkspStatusFilter,
  FkspTypeFilter,
  FkspFilledFilter,
  FkspEkonomSortField,
} from '../lib/fkspConstants'
import { FKSP_LIST_FETCH_LIMIT } from '../lib/fkspConstants'

function financistDatumToInput(value: string | null | undefined): string {
  if (!value) return ''
  const raw = value.slice(0, 10)
  return /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : ''
}

function parseDoplacenoInput(raw: string): number | null {
  const trimmed = raw.trim().replace(/\s/g, '').replace(',', '.')
  if (!trimmed) return null
  const n = Number(trimmed)
  return Number.isFinite(n) ? Math.round(n * 100) / 100 : null
}

type FinancistFieldEditorProps = {
  req: FkspRequest
  savingId: string | null
  onSave: (req: FkspRequest, fields: { doplaceno?: number | null; datum?: string | null; note?: string | null }) => Promise<void>
}

const FinancistDoplacenoCell: React.FC<FinancistFieldEditorProps> = ({ req, savingId, onSave }) => {
  const [draft, setDraft] = useState(
    req.financist_doplaceno !== null && req.financist_doplaceno !== undefined
      ? String(req.financist_doplaceno)
      : '',
  )

  useEffect(() => {
    setDraft(
      req.financist_doplaceno !== null && req.financist_doplaceno !== undefined
        ? String(req.financist_doplaceno)
        : '',
    )
  }, [req.financist_doplaceno, req.id])

  const commit = async () => {
    const parsed = parseDoplacenoInput(draft)
    const current = req.financist_doplaceno ?? null
    if (parsed === current) return
    if (draft.trim() && parsed === null) {
      setDraft(current !== null && current !== undefined ? String(current) : '')
      return
    }
    await onSave(req, { doplaceno: parsed })
  }

  return (
    <TextField
      size="small"
      type="text"
      inputMode="decimal"
      placeholder="Kč"
      value={draft}
      disabled={savingId === req.id}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => { void commit() }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          ;(e.target as HTMLInputElement).blur()
        }
      }}
      sx={{
        width: 108,
        '& .MuiInputBase-input': {
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
        },
      }}
      inputProps={{ 'aria-label': 'Doplaceno' }}
    />
  )
}

const FinancistDatumCell: React.FC<FinancistFieldEditorProps> = ({ req, savingId, onSave }) => {
  const [draft, setDraft] = useState(financistDatumToInput(req.financist_datum))

  useEffect(() => {
    setDraft(financistDatumToInput(req.financist_datum))
  }, [req.financist_datum, req.id])

  const commit = async () => {
    const next = draft.trim() || null
    const current = financistDatumToInput(req.financist_datum) || null
    if (next === current) return
    await onSave(req, { datum: next })
  }

  return (
    <TextField
      size="small"
      type="date"
      value={draft}
      disabled={savingId === req.id}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => { void commit() }}
      sx={{ width: 148 }}
      InputLabelProps={{ shrink: true }}
      inputProps={{ 'aria-label': 'Datum zaplacení' }}
    />
  )
}

const FinancistNoteCell: React.FC<FinancistFieldEditorProps> = ({ req, savingId, onSave }) => {
  const [draft, setDraft] = useState(req.financist_note || '')

  useEffect(() => {
    setDraft(req.financist_note || '')
  }, [req.financist_note, req.id])

  const commit = async () => {
    const next = draft.trim() || null
    const current = (req.financist_note || '').trim() || null
    if (next === current) return
    await onSave(req, { note: next })
  }

  return (
    <TextField
      size="small"
      multiline
      minRows={1}
      maxRows={4}
      value={draft}
      disabled={savingId === req.id}
      placeholder="Poznámka…"
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => { void commit() }}
      sx={{ width: 200, minWidth: 160 }}
      inputProps={{ 'aria-label': 'Poznámka ekonoma', maxLength: 5000 }}
    />
  )
}

export const FkspEkonomView: React.FC = () => {
  const { user } = useAuth()
  const [requests, setRequests] = useState<FkspRequest[]>([])
  const [listLoaded, setListLoaded] = useState(false)
  const [toast, setToast] = useState<{ show: boolean; msg: string; type: 'success' | 'error' }>({
    show: false,
    msg: '',
    type: 'success',
  })
  const [selectedReq, setSelectedReq] = useState<FkspRequest | null>(null)
  const [listSearch, setListSearch] = useState('')
  const [listCreatedFrom, setListCreatedFrom] = useState('')
  const [listCreatedTo, setListCreatedTo] = useState('')
  const [listDateNewestFirst, setListDateNewestFirst] = useState(true)
  const [typeFilter, setTypeFilter] = useState<FkspTypeFilter>('all')
  const [processedFilter, setProcessedFilter] = useState<FkspProcessedFilter>('all')
  const [statusFilter, setStatusFilter] = useState<FkspStatusFilter>('all')
  const [benefitSubtypeFilter, setBenefitSubtypeFilter] = useState('')
  const [doplacenoFilter, setDoplacenoFilter] = useState<FkspFilledFilter>('all')
  const [datumFilter, setDatumFilter] = useState<FkspFilledFilter>('all')
  const [sortField, setSortField] = useState<FkspEkonomSortField>('created_at')
  const [savingId, setSavingId] = useState<string | null>(null)
  const [exporting, setExporting] = useState<'xlsx' | 'pdf' | null>(null)
  const [listPage, setListPage] = useState(0)

  useEffect(() => {
    fetchRequests()
  }, [user?.role, user?.admin_type])

  const fetchRequests = async () => {
    try {
      const isItAdmin = user?.role === 'admin' && user?.admin_type === 'spravce_it'
      const isFinancist = hasFinancistAccess(user)
      const data = await loadFkspRequests(isItAdmin || isFinancist ? 'fksp_financist' : undefined, {
        limit: FKSP_LIST_FETCH_LIMIT,
      })
      setRequests(data)
    } catch (err: any) {
      setToast({ show: true, msg: err.message, type: 'error' })
    } finally {
      setListLoaded(true)
    }
  }

  useDeepLinkById({
    items: requests,
    ready: listLoaded,
    onFound: (r) => setSelectedReq(r),
    onNotFound: () =>
      setToast({ show: true, msg: 'Žádost nenalezena nebo již byla zpracována.', type: 'error' }),
  })

  const handleDownloadPdf = async (req: FkspRequest) => {
    try {
      setToast({ show: true, msg: 'Generuji PDF...', type: 'success' })
      await downloadFkspPdf(req.id, req.type)
      setToast({ show: true, msg: 'PDF staženo', type: 'success' })
    } catch (err: any) {
      setToast({ show: true, msg: 'Chyba PDF: ' + err.message, type: 'error' })
    }
  }

  const handleToggleProcessed = async (req: FkspRequest) => {
    const isProcessed = !!req.financist_processed_at
    try {
      if (isProcessed) {
        await unmarkFkspRequestProcessed(req.id)
        setToast({ show: true, msg: 'Označení zpracování bylo zrušeno', type: 'success' })
      } else {
        await markFkspRequestProcessed(req.id)
        setToast({ show: true, msg: 'Označeno jako zpracováno', type: 'success' })
      }
      await fetchRequests()
      if (selectedReq?.id === req.id) {
        setSelectedReq(
          isProcessed
            ? { ...req, financist_processed_at: null, financist_processed_by: null, financist_processed_note: null }
            : { ...req, financist_processed_at: new Date().toISOString() },
        )
      }
    } catch (err: any) {
      setToast({
        show: true,
        msg: err.message || (isProcessed ? 'Chyba při rušení označení' : 'Chyba: nelze označit jako zpracováno'),
        type: 'error',
      })
    }
  }

  const handleSaveFinancistFields = useCallback(
    async (req: FkspRequest, fields: { doplaceno?: number | null; datum?: string | null; note?: string | null }) => {
      setSavingId(req.id)
      try {
        const res = await updateFkspFinancistFields(req.id, fields)
        setRequests((prev) =>
          prev.map((r) =>
            r.id === req.id
              ? {
                  ...r,
                  financist_doplaceno: res.financist_doplaceno ?? null,
                  financist_datum: res.financist_datum ?? null,
                  financist_note: res.financist_note ?? null,
                }
              : r,
          ),
        )
        if (selectedReq?.id === req.id) {
          setSelectedReq((cur) =>
            cur
              ? {
                  ...cur,
                  financist_doplaceno: res.financist_doplaceno ?? null,
                  financist_datum: res.financist_datum ?? null,
                  financist_note: res.financist_note ?? null,
                }
              : cur,
          )
        }
      } catch (err: any) {
        setToast({ show: true, msg: err.message || 'Chyba při ukládání', type: 'error' })
        throw err
      } finally {
        setSavingId(null)
      }
    },
    [selectedReq?.id],
  )

  const displayRequests = useMemo(
    () =>
      filterAndSortFkspRequests(requests, {
        searchQuery: listSearch,
        dateNewestFirst: listDateNewestFirst,
        createdFrom: listCreatedFrom,
        createdTo: listCreatedTo,
        typeFilter,
        processedFilter,
        statusFilter,
        benefitSubtypeFilter,
        doplacenoFilter,
        datumFilter,
        sortField,
      }),
    [
      requests,
      listSearch,
      listDateNewestFirst,
      listCreatedFrom,
      listCreatedTo,
      typeFilter,
      processedFilter,
      statusFilter,
      benefitSubtypeFilter,
      doplacenoFilter,
      datumFilter,
      sortField,
    ],
  )

  useEffect(() => {
    setListPage(0)
  }, [
    listSearch,
    listDateNewestFirst,
    listCreatedFrom,
    listCreatedTo,
    typeFilter,
    processedFilter,
    statusFilter,
    benefitSubtypeFilter,
    doplacenoFilter,
    datumFilter,
    sortField,
  ])

  const pagedRequests = useMemo(
    () => fkspPageSlice(displayRequests, listPage),
    [displayRequests, listPage],
  )

  const handleExportList = async (format: 'xlsx' | 'pdf') => {
    if (displayRequests.length === 0) {
      setToast({ show: true, msg: 'Žádné žádosti k exportu', type: 'error' })
      return
    }
    setExporting(format)
    try {
      const rows = buildFkspEkonomExportRows(displayRequests)
      await downloadFkspEkonomListExport(format, rows)
      setToast({
        show: true,
        msg: format === 'xlsx' ? 'Excel stažen' : 'PDF staženo',
        type: 'success',
      })
    } catch (err: any) {
      setToast({ show: true, msg: err.message || 'Chyba exportu', type: 'error' })
    } finally {
      setExporting(null)
    }
  }

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 'bold' }}>
        FKSP — Hlavní ekonom
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Přehled všech žádostí FKSP. Ekonom doplňuje částku doplacenou zaměstnancem a datum zaplacení; uložení proběhne po opuštění pole.
      </Typography>

      <FkspRequestsToolbar
        search={listSearch}
        onSearchChange={setListSearch}
        createdFrom={listCreatedFrom}
        onCreatedFromChange={setListCreatedFrom}
        createdTo={listCreatedTo}
        onCreatedToChange={setListCreatedTo}
        sortField={sortField}
        onSortFieldChange={setSortField}
        dateNewestFirst={listDateNewestFirst}
        onDateSortChange={setListDateNewestFirst}
        extendedFilters
        ekonomFilters
        typeFilter={typeFilter}
        onTypeFilterChange={(v) => {
          setTypeFilter(v)
          setBenefitSubtypeFilter('')
        }}
        processedFilter={processedFilter}
        onProcessedFilterChange={setProcessedFilter}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        benefitSubtypeFilter={benefitSubtypeFilter}
        onBenefitSubtypeFilterChange={setBenefitSubtypeFilter}
        doplacenoFilter={doplacenoFilter}
        onDoplacenoFilterChange={setDoplacenoFilter}
        datumFilter={datumFilter}
        onDatumFilterChange={setDatumFilter}
      />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<TableViewIcon />}
          disabled={displayRequests.length === 0 || exporting !== null}
          onClick={() => handleExportList('xlsx')}
        >
          {exporting === 'xlsx' ? 'Generuji Excel…' : `Stáhnout Excel (${displayRequests.length})`}
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<PictureAsPdfIcon />}
          disabled={displayRequests.length === 0 || exporting !== null}
          onClick={() => handleExportList('pdf')}
        >
          {exporting === 'pdf' ? 'Generuji PDF…' : `Stáhnout PDF (${displayRequests.length})`}
        </Button>
      </Box>

      <Card>
        <CardContent>
          <Typography variant="h6" mb={3} color="primary">
            Všechny žádosti ({displayRequests.length})
          </Typography>
          <TableContainer component={Paper} elevation={0} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.50' }}>
                  <TableCell>Datum podání</TableCell>
                  <TableCell>Žadatel</TableCell>
                  <TableCell>Typ</TableCell>
                  <TableCell>Stav</TableCell>
                  <TableCell align="right">Částka</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Částka doplacená zaměstnancem">
                      <span>Doplaceno</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Datum, kdy byla žádost uhrazena (zapisuje ekonom)">
                      <span>Datum zaplacení</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Interní poznámka ekonoma nebo referenta EKO">
                      <span>Poznámka</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="center">Zpracováno</TableCell>
                  <TableCell align="center">PDF</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                      Žádné žádosti
                    </TableCell>
                  </TableRow>
                ) : displayRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                      Žádná žádost neodpovídá vyhledávání
                    </TableCell>
                  </TableRow>
                ) : (
                  pagedRequests.map((req) => (
                    <TableRow
                      key={req.id}
                      hover
                      onClick={() => setSelectedReq(req)}
                      sx={{
                        cursor: 'pointer',
                        ...(req.financist_processed_at ? { bgcolor: '#fff9c4' } : {}),
                      }}
                    >
                      <TableCell>{format(new Date(req.created_at), 'dd.MM.yyyy')}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {req.requester_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Os. č: {req.personal_number}
                        </Typography>
                      </TableCell>
                      <TableCell>{req.type === 'benefity' ? 'Benefity' : 'Připojištění'}</TableCell>
                      <TableCell>
                        <Chip
                          label={fkspStatusLabelCs(req.status)}
                          color={fkspStatusChipColor(req.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {req.type === 'benefity' ? req.amount : req.fksp_contribution} Kč
                      </TableCell>
                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <FinancistDoplacenoCell req={req} savingId={savingId} onSave={handleSaveFinancistFields} />
                        </Box>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <FinancistDatumCell req={req} savingId={savingId} onSave={handleSaveFinancistFields} />
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <FinancistNoteCell req={req} savingId={savingId} onSave={handleSaveFinancistFields} />
                      </TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        {req.status === 'director_approved' ? (
                          <Tooltip
                            title={
                              req.financist_processed_at
                                ? `Zpracováno: ${format(new Date(req.financist_processed_at), 'dd.MM.yyyy HH:mm')}`
                                : 'Označit jako zpracováno'
                            }
                          >
                            <Checkbox
                              checked={!!req.financist_processed_at}
                              onChange={() => handleToggleProcessed(req)}
                              size="small"
                              inputProps={{ 'aria-label': 'Zpracováno' }}
                              sx={{ p: 0.5 }}
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <Box sx={{ display: 'inline-flex', gap: 0.25 }}>
                          <Tooltip title="PDF pro tisk">
                            <IconButton color="secondary" size="small" onClick={() => handleDownloadPdf(req)}>
                              <PictureAsPdfIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <FkspTablePagination
            count={displayRequests.length}
            page={listPage}
            onPageChange={setListPage}
          />
        </CardContent>
      </Card>

      <Snackbar open={toast.show} autoHideDuration={6000} onClose={() => setToast({ ...toast, show: false })}>
        <Alert severity={toast.type} onClose={() => setToast({ ...toast, show: false })}>
          {toast.msg}
        </Alert>
      </Snackbar>

      <FkspRequestModal
        open={selectedReq !== null}
        onClose={() => setSelectedReq(null)}
        request={selectedReq}
        canApprove={false}
        onApprove={() => {}}
        onReject={() => {}}
        onDownloadPdf={async (req) => {
          try {
            await downloadFkspPdf(req.id, req.type)
          } catch (e: any) {
            setToast({ show: true, msg: e?.message || 'Chyba PDF', type: 'error' })
          }
        }}
      />
    </Box>
  )
}
